import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getClientEnv } from "@nosible/shared";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Separator } from "@nosible/shared/components/ui/separator";
import { NameLogoHorizontal, WorldPrimaryLogo } from "@/account/components/Logo";
import { sharedAuthRedirect } from "@/account/lib/auth-redirect";
import { useSession, authClient } from "@/account/lib/auth-client";
import { useTurnstile } from "@/account/hooks/use-turnstile";

const magicLinkSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type MagicLinkFormData = z.infer<typeof magicLinkSchema>;

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-11.386 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export function Login() {
  const { data: session, isPending } = useSession();
  const [serverError, setServerError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const {
    turnstileRef,
    token: turnstileToken,
    error: turnstileError,
    reset: resetTurnstile,
    enabled: turnstileEnabled,
  } = useTurnstile({ action: "login" });

  const captchaHeaders: Record<string, string> = turnstileToken ? { "x-captcha-response": turnstileToken } : {};

  const magicLinkForm = useForm<MagicLinkFormData>({
    resolver: zodResolver(magicLinkSchema),
  });

  const getRedirectUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect") ?? params.get("callbackURL");
    return sharedAuthRedirect(redirect, getClientEnv());
  };

  useEffect(() => {
    if (session && !isPending && typeof window !== "undefined") {
      window.location.href = getRedirectUrl();
    }
  }, [session, isPending]);

  const handleSocialSignIn = async (provider: "github" | "google" | "microsoft" | "linkedin") => {
    setServerError(null);
    setSocialLoading(provider);
    const redirectUrl = getRedirectUrl();

    const { error } = await authClient.signIn.social({
      provider,
      callbackURL: redirectUrl,
    });

    if (error) {
      setServerError(error.message || `Failed to sign in with ${provider}`);
      setSocialLoading(null);
    }
  };

  const onMagicLinkSubmit = async (data: MagicLinkFormData) => {
    setServerError(null);
    const redirectUrl = getRedirectUrl();

    const { error } = await authClient.signIn.magicLink({
      email: data.email,
      callbackURL: redirectUrl,
      fetchOptions: { headers: captchaHeaders },
    });

    if (error) {
      setServerError(error.message || "Failed to send magic link");
      resetTurnstile();
      return;
    }

    setSubmittedEmail(data.email);
    setMagicLinkSent(true);
  };

  if (isPending) {
    return <div className="auth-page"><div className="auth-page__loading" role="status">Loading…</div></div>;
  }

  if (magicLinkSent) {
    return (
      <div className="auth-page">
        <Card className="auth-page__card auth-page__card--compact">
          <CardHeader className="auth-page__header auth-page__header--email-sent">
            <NameLogoHorizontal className="auth-page__wordmark" aria-label="NOSIBLE" />
            <CardTitle className="auth-page__title">Check your email</CardTitle>
            <CardDescription className="auth-page__description">
              If this address is registered, a sign-in link is on its way to <strong>{submittedEmail}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="auth-page__content auth-page__content--email-sent">
            <button className="auth-page__text-action" type="button" onClick={() => setMagicLinkSent(false)}>
              Use a different email
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Card className="auth-page__card">
        <CardHeader className="auth-page__header">
          <WorldPrimaryLogo className="auth-page__wordmark" aria-label="NOSIBLE" />
          <p className="auth-page__eyebrow">World · secure access</p>
          <CardTitle className="auth-page__title">Sign in</CardTitle>
        </CardHeader>
        <CardContent className="auth-page__content">
          <div className="auth-page__providers">
            <Button type="button" variant="outline" className="auth-page__provider" disabled={socialLoading !== null} onClick={() => handleSocialSignIn("google")}>
              <GoogleIcon />
              <span>{socialLoading === "google" ? "Connecting…" : "Continue with Google"}</span>
              <span className="auth-page__provider-arrow" aria-hidden="true">→</span>
            </Button>
            <Button type="button" variant="outline" className="auth-page__provider" disabled={socialLoading !== null} onClick={() => handleSocialSignIn("github")}>
              <GitHubIcon />
              <span>{socialLoading === "github" ? "Connecting…" : "Continue with GitHub"}</span>
              <span className="auth-page__provider-arrow" aria-hidden="true">→</span>
            </Button>
          </div>

          <div className="auth-page__divider" aria-hidden="true">
            <Separator />
            <span>or</span>
            <Separator />
          </div>

          <form className="auth-page__form" onSubmit={magicLinkForm.handleSubmit(onMagicLinkSubmit)}>
            <Label className="auth-page__label" htmlFor="email">Email address</Label>
            <Input id="email" className="auth-page__input" type="email" placeholder="you@example.com" {...magicLinkForm.register("email")} />
            {magicLinkForm.formState.errors.email && <p className="auth-page__error" role="alert">{magicLinkForm.formState.errors.email.message}</p>}
            {serverError && <p className="auth-page__error" role="alert">{serverError}</p>}
            {turnstileEnabled && <div ref={turnstileRef} className="auth-page__turnstile" />}
            {turnstileError && <p className="auth-page__error" role="alert">{turnstileError}</p>}
            <Button type="submit" className="auth-page__primary" disabled={magicLinkForm.formState.isSubmitting}>
              {magicLinkForm.formState.isSubmitting ? "Sending…" : "Continue with email"}
            </Button>
            <p className="auth-page__security-note">Security checks appear only when needed.</p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
